import { useStore } from "@nanostores/preact"
import { atom, map } from "nanostores"

import { fetchData } from "utils/fetch-data"
import { $user, useUser } from "./user"

type Price = {
  unit_amount: number
  currency: string
  price_id: string
}

type Currency = {
  acronym: string
  prefix: string
}

type IsoCountry = {
  iso: string
  name: string
  currency: string
}

export type Support = {
  stripe_publishable_key: string
  default_iso_country: string
  iso_countries: IsoCountry[]
  currencies: Currency[]
  products: {
    annually: {
      prices: {
        [currency: string]: Price
      }
    }
    monthly: {
      prices: {
        [currency: string]: Price
      }
    }
  }
  user: {
    premium: boolean
    signedIn: boolean
    emailVerified: boolean
    email: null
    forcedCurrency: null
    premiumExpires: null
  }
  messages: {
    [key: string]: string
  }
}

export const $support = atom<Support | null>(null)

export const $supportLoadingStates = map<{
  fulfilled: boolean
  loading: boolean
  error: any | null
}>({
  fulfilled: false,
  loading: false,
  error: null,
})

export async function prefetchSupport(): Promise<void> {
  try {
    $supportLoadingStates.setKey("loading", true)
    $supportLoadingStates.setKey("error", null)

    const data = await fetchData("/sign_up/support")

    $support.set(data)
    $user.setKey("premium", data.user.premium)
    $user.setKey("signedIn", data.user.signedIn)
    $user.setKey("forcedCurrency", data.user.forcedCurrency)

    $supportLoadingStates.setKey("fulfilled", true)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    $supportLoadingStates.setKey("error", error)
  } finally {
    $supportLoadingStates.setKey("loading", false)
  }
}

export function useSupport(): Support {
  const support = useStore($support)
  if (support == null) {
    throw new Error("$supportLoadingStates must be fulfilled before accessing useSupport")
  }

  return support
}

export function usePrices() {
  const support = useSupport()
  const { isoCountry, forcedCurrency } = useUser()

  let iso = "US"
  let currencyAcronym = "USD"
  if (forcedCurrency != null) {
    const country = support.iso_countries.find(({ currency }) => forcedCurrency === currency)
    if (country != null) {
      iso = country.iso
      currencyAcronym = country.currency
    }
  } else {
    const country = support.iso_countries.find(
      ({ iso: shadowIso }) => (isoCountry ?? support.default_iso_country) === shadowIso,
    )
    if (country != null) {
      iso = country.iso
      currencyAcronym = country.currency
    }
  }

  const currency = support.currencies.find(({ acronym }) => currencyAcronym === acronym)

  const monthly = support.products.monthly.prices[currencyAcronym]
  const annually = support.products.annually.prices[currencyAcronym]

  return { iso, currency, monthly, annually }
}
